/**
 * Dashboard — what happens in the seconds after the assessment ends.
 *
 * The learner lands here with `?completed=1`. Their on-device result is
 * already viewable, but the sealed canonical report is still being built.
 * Rather than claiming "your results are ready" before that's true, this
 * banner tells the truth in three states:
 *
 *   working  — the report is being assembled (spinner, preview still open)
 *   slow     — it's taking longer than it should (retry offered)
 *   failed   — a stage failed (retry + direct link to the report)
 *   ready    — sealed report landed, or the run never needed one
 *
 * Presentation only. All state comes from `useCanonicalTakeover`.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, RefreshCw, Sparkles, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { parseBand, bandParam } from "@/features/assessment-v2/band";
import { useCanonicalTakeover } from "@/features/assessment-v2/useCanonicalReport";

/** Past this, "still working" stops being reassuring and starts being a bug. */
const SLOW_AFTER_MS = 45_000;

export function ResultsReadyBanner({ bandParamValue }: { bandParamValue: string | null }) {
  const band = parseBand(bandParamValue);
  const takeover = useCanonicalTakeover(band);
  const [slow, setSlow] = useState(false);

  const working = takeover.disposition === "started" && !takeover.ready && !takeover.failedStage;

  useEffect(() => {
    if (!working) { setSlow(false); return; }
    const t = window.setTimeout(() => setSlow(true), SLOW_AFTER_MS);
    return () => window.clearTimeout(t);
  }, [working, takeover.retrying]);

  const resultHref = `/assessment/v2/result?band=${encodeURIComponent(bandParam(band))}`;

  if (takeover.failedStage || (working && slow)) {
    const hardFail = Boolean(takeover.failedStage);
    return (
      <section
        aria-label="Your results are still being generated"
        data-testid="dashboard-results-retry"
        className="rounded-3xl border border-warning/40 bg-warning/10 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4"
      >
        <div className="h-11 w-11 rounded-2xl bg-warning/20 flex items-center justify-center shrink-0">
          <TriangleAlert className="h-5 w-5 text-warning" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-lg sm:text-xl font-bold">
            {hardFail ? "One step got stuck" : "This is taking longer than usual"}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Every answer you gave is saved. We just need another go at building your full
            report — nothing is lost, and you can read your results in the meantime.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button
            onClick={takeover.retry}
            disabled={takeover.retrying}
            size="lg"
            variant="outline"
            className="rounded-full h-12 px-5"
          >
            {takeover.retrying
              ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              : <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />}
            {takeover.retrying ? "Trying again…" : "Try again"}
          </Button>
          <Button asChild size="lg" className="rounded-full h-12 px-5">
            <Link to={resultHref}>
              See my results <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </section>
    );
  }

  if (working) {
    return (
      <section
        aria-label="Your results are being generated"
        aria-busy="true"
        role="status"
        data-testid="dashboard-results-loading"
        className="rounded-3xl border border-primary/30 bg-primary/5 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4"
      >
        <div className="h-11 w-11 rounded-2xl bg-primary/15 flex items-center justify-center shrink-0">
          <Loader2 className="h-5 w-5 text-primary animate-spin" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-lg sm:text-xl font-bold">
            Putting your results together
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            We're reading every answer you gave and matching it across 5,877 kinds of work.
            This usually takes a few seconds — you can start reading now.
          </p>
          <div className="mt-3 h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-primary/15">
            <div className="h-full w-2/3 animate-pulse rounded-full bg-primary/70 motion-reduce:w-full motion-reduce:animate-none" />
          </div>
        </div>
        <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-6 shrink-0">
          <Link to={resultHref}>
            Read the preview <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
          </Link>
        </Button>
      </section>
    );
  }

  return (
    <section
      aria-label="Your results are ready"
      data-testid="dashboard-results-ready"
      className="rounded-3xl border border-primary/40 bg-primary/10 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4"
    >
      <div className="h-11 w-11 rounded-2xl bg-primary/20 flex items-center justify-center shrink-0">
        <Sparkles className="h-5 w-5 text-primary" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <h2 className="font-display text-lg sm:text-xl font-bold">
          Nice work — your results are ready
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Your careers, archetypes and next moves are waiting for you.
        </p>
      </div>
      <Button asChild size="lg" className="rounded-full h-12 px-6 shrink-0">
        <Link to={resultHref}>
          See my results <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
        </Link>
      </Button>
    </section>
  );
}

export default ResultsReadyBanner;
